/**
 * Site list grid — customer+plant-scoped list of sites (structures).
 *
 * Server-backed via EntityGrid over odl-app/sites/. Customer and site-group
 * scope are enforced through extraParams. Supports bulk deactivate, transfer,
 * and reactivate (select-all + explicit ids) via the odl-app sites write slice.
 * Staff see an active/deactivated status filter; toolbar includes upload and
 * clone-settings actions on desktop. Mobile renders each row as a card.
 *
 * Shared across tiers: the customer tier mounts it as-is; the services tier
 * mounts it against the extended odl-app/job-sites/ descriptor (`basePath`)
 * with its own row destination (`sitePath`) and an `extension` bag —
 * `{ params, quickFilters, fieldOverrides, extraColumns, excludeFields,
 * gridProps, statsHeader, demoteBaseQuickFilters, hideWriteActions, mobile,
 * onResetAll, onGridReady, onParamsChange }` — layered onto the base grid.
 * The extension object must be memoized by the caller;
 * `params` ride with the list fetch and select-all bulk filters (never the
 * scope-level stats call); `statsHeader` replaces the health stat cards
 * (null drops them); `demoteBaseQuickFilters` moves the base quick chips
 * into the More-filters popover so the tier's own chips lead;
 * `onParamsChange(extraParams)` receives the composed list params whenever
 * they change, so a tier can aim sibling fetches (e.g. its own stat cards)
 * at exactly the set the grid is listing;
 * `mobile.getSections(row)` returns the card sections the tier adds on a
 * phone, where its `extraColumns` do not render.
 *
 * A FilterBar exposes the dimension filters this page owns — unit status,
 * alarm category, carrier/radio, device model, and a staff-only feature filter
 * — alongside the filters the descriptor publishes, which EntityGrid hands in
 * as ready controls already wired to the card-filter model. A published filter
 * whose param this page already draws a control for is dropped rather than
 * doubled.
 *
 * Clickable summary cards (operational, alarming, need
 * maintenance) read their counts from odl-app/sites/stats/ and apply their
 * filter set on click. The stats call is scoped to the active customer/plant
 * and the active/deactivated status so the card and filter-option counts stay
 * stable as the dimension filters narrow the grid.
 *
 * Restores the legacy column rendering from the self-contained odl-app row
 * payload: per-alarm-category badges, the live unit-status dot + chip, add-on
 * icons (WiFi/Ethernet/Camera/Intrusion/Weather), and a client-derived
 * last-alarm timestamp.
 *
 * Remaining parity gap: search covers name + description + location_description
 * only (not radio iccid/mac).
 *
 * Contains:
 * - SiteGrid     — EntityGrid over odl-app/sites/ (default export)
 * - SiteListItem — mobile card
 */

import { Capacitor } from '@capacitor/core';
import ConfirmationNumberOutlinedIcon from '@mui/icons-material/ConfirmationNumberOutlined';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import LockIcon from '@mui/icons-material/Lock';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import RestoreIcon from '@mui/icons-material/Restore';
import SettingsEthernetIcon from '@mui/icons-material/SettingsEthernet';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import WbSunnyRoundedIcon from '@mui/icons-material/WbSunnyRounded';
import WifiIcon from '@mui/icons-material/Wifi';
import { Box, Button, Chip, Stack, Tooltip, Typography, useMediaQuery, useTheme } from '@mui/material';
import { fromJS } from 'immutable';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { fetchSitesStats } from '@sophocles/api/sites';
import { useUser } from '@sophocles/app';
import FilterBar from '@sophocles/components/FilterBar';
import { buildStatusQuickFilter } from '@sophocles/components/FilterBar/statusQuickFilter';
import UnitStatusDot, { unitStatusChipColor, unitStatusLabel } from '@sophocles/components/UnitStatusDot';
import UploadGroupDataDialog from '@sophocles/components/UploadGroupDataDialog';
// eslint-disable-next-line no-restricted-imports -- genuine warning, not a destructive confirm
import WarningDialog from '@sophocles/components/WarningDialog';
import { EntityGrid } from '@sophocles/data-grids';
import AlarmChipCell from '@sophocles/data-grids/components/cells/AlarmChipCell';
import GridNameCell from '@sophocles/data-grids/components/cells/GridNameCell';
import GridStatusCell from '@sophocles/data-grids/components/cells/GridStatusCell';
import { composeMobileCardSections } from '@sophocles/data-grids/components/mobile/cardSections';
import GridMobileCard from '@sophocles/data-grids/components/mobile/GridMobileCard';
import { ToolbarIconButton } from '@sophocles/data-grids/components/toolbar';
import { toJsonApiParams } from '@sophocles/data-grids/datasource/params';
import { SELECTION_ACTION_SX, resolveSelectionRows } from '@sophocles/data-grids/datasource/selectionRows';
import useColumnVisibilityModel from '@sophocles/data-grids/datasource/useColumnVisibilityModel';
import useGridCardFilters from '@sophocles/data-grids/datasource/useGridCardFilters';
import { useGridSessionKey } from '@sophocles/data-grids/datasource/useGridPreferenceKey';
import useGridRefreshRef from '@sophocles/data-grids/datasource/useGridRefreshRef';
import useGridSelectAll from '@sophocles/data-grids/datasource/useGridSelectAll';
import useGridStats from '@sophocles/data-grids/datasource/useGridStats';
import useStatCardOrderModel from '@sophocles/data-grids/datasource/useStatCardOrderModel';
import useStatCardVisibilityModel from '@sophocles/data-grids/datasource/useStatCardVisibilityModel';
import { omitOwnedFilterControls } from '@sophocles/data-grids/grid/descriptorFilters';
import {
  useDeactivateSitesMutation,
  useReactivateSitesMutation,
  useTransferSitesMutation,
} from '@sophocles/features/entities/site';
import { selectAlarmCategories, selectAlarmDefinitions } from '@sophocles/features/reference';
import { addToast } from '@sophocles/features/toast';
import usePersistedState from '@sophocles/hooks/usePersistedState';
import useRelayLoadTerm from '@sophocles/hooks/useRelayLoadTerm';
import useSiteTerm from '@sophocles/hooks/useSiteTerm';
import useTenantTerms from '@sophocles/hooks/useTenantTerms';
import { compareSeverity, severityStatusFamily } from '@sophocles/utils/alarmSeverity';
import { tAlarm as translateAlarm } from '@sophocles/utils/alarmTranslation';
import { formatDateTime } from '@sophocles/utils/formatUtils';
import { userPermCheck } from '@sophocles/utils/userPermissionUtils';
import CloneUnitModal from '@sophocles/views/sites/components/CloneUnitModal';
import CreateSiteDialog from '@sophocles/views/sites/dialogs/CreateSiteDialog';

import BulkDeactivateSitesDialog from './BulkDeactivateSitesDialog';
import TransferSitesDialog from './TransferSitesDialog';

// A page that draws no filter control of its own still hands the bar a list;
// one frozen empty array keeps that from re-rendering the toolbar every pass.
const NO_PAGE_FILTERS = Object.freeze([]);

// Add-on icons surfaced from the self-contained row payload: connectivity from
// the comm_modes bitmask, camera/intrusion booleans, and the effective Weather
// feature flag.
function renderAddonIcons(row, t) {
  const addons = [];
  const commModes = row?.comm_modes ?? 0;

  if (commModes & 0b0100)
    addons.push(<WifiIcon key="wifi" fontSize="small" titleAccess={t('sites.connectionWifi', 'WiFi')} />);
  if (commModes & 0b0010)
    addons.push(
      <SettingsEthernetIcon key="ethernet" fontSize="small" titleAccess={t('sites.connectionEthernet', 'Ethernet')} />,
    );
  if (row?.has_camera)
    addons.push(<PhotoCameraIcon key="camera" fontSize="small" titleAccess={t('sites.cameraView', 'Camera View')} />);
  if (row?.has_intrusion)
    addons.push(
      <LockIcon key="intrusion" fontSize="small" titleAccess={t('sites.intrusionSensor', 'Intrusion Sensor')} />,
    );
  if (row?.features?.some(f => f.key === 'WEATHER' && f.is_effectively_enabled))
    addons.push(
      <WbSunnyRoundedIcon key="weather" fontSize="small" titleAccess={t('sites.smartWeather', 'Smart-Weather')} />,
    );

  return addons.length > 0 ? (
    <Stack direction="row" spacing={0.5} sx={{ color: 'text.secondary' }}>
      {addons}
    </Stack>
  ) : null;
}

// Group the row's alarm states by category, tracking the highest severity and
// instance count per category (shown as a qty when > 1, like the ops
// dashboard), sorted by the category's display order. Labels resolve through
// the id → translated-label maps (reference catalog + i18n, so commercial
// tenants get their vocabulary); the row's backend strings are the fallback
// for ids missing from the catalogs.
function resolveAlarmBadges(row, categoryLabelById, alarmLabelById) {
  const categoryMap = new Map();
  (row?.alarm_states ?? []).forEach(state => {
    if (state.category_id == null) return;
    const key = String(state.category_id);
    const severity = state.severity || 'warning';
    const alarmLabel = alarmLabelById.get(String(state.alarm_id)) ?? state.display_name;
    const existing = categoryMap.get(key);
    if (!existing) {
      categoryMap.set(key, {
        categoryName: categoryLabelById.get(key) ?? state.category_name,
        displayOrder: state.category_display_order ?? 0,
        severity,
        count: 1,
        displayNames: alarmLabel ? [alarmLabel] : [],
      });
    } else {
      existing.count += 1;
      if (compareSeverity(severity, existing.severity) < 0) existing.severity = severity;
      if (alarmLabel) existing.displayNames.push(alarmLabel);
    }
  });

  return Array.from(categoryMap.entries())
    .map(([id, entry]) => ({ id, ...entry }))
    .sort((a, b) => a.displayOrder - b.displayOrder)
    .map(entry => ({
      id: entry.id,
      label: entry.count > 1 ? `${entry.categoryName} ×${entry.count}` : entry.categoryName,
      tooltip: entry.displayNames.join(', '),
      color: severityStatusFamily(entry.severity),
    }));
}

// Latest alarm timestamp across the row's alarm states (FE-derived — no BE
// field). Returns null when the site has no active alarms.
function getLastAlarm(row) {
  let latest = null;
  for (const state of row?.alarm_states ?? []) {
    const ts = state?.timestamp;
    if (ts && (latest === null || ts > latest)) latest = ts;
  }
  return latest;
}

// ---------------------------------------------------------------------------
// Mobile list item
// ---------------------------------------------------------------------------

function SiteListItem({ row, to, extraSections }) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const baseChips = [
    row.device_label ? <Chip key="device" size="small" variant="outlined" label={row.device_label} /> : null,
    renderAddonIcons(row, t),
  ];
  const baseMeta = row.iccid ? [{ key: 'iccid', label: t('sites.iccid', 'ICCID'), value: row.iccid }] : [];
  const { meta, chips, actions } = composeMobileCardSections({ meta: baseMeta, chips: baseChips }, extraSections);

  return (
    <GridMobileCard
      onClick={() => navigate(to)}
      leading={<UnitStatusDot status={row.status} size={10} />}
      primary={row.name}
      secondary={row.description || null}
      meta={meta}
      chips={chips}
      actions={actions}
      status={
        <GridStatusCell
          color={row.deactivated ? 'neutral' : unitStatusChipColor(row.status)}
          label={row.deactivated ? t('general.deactivated') : unitStatusLabel(row.status, t)}
        />
      }
    />
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export default function SiteGrid({
  customer,
  selectedPlant,
  user,
  defaultStatus = 'active',
  persistScope = 'structureGrid',
  basePath = 'sites',
  sitePath = null,
  extension = null,
}) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { isStaff, permissions } = useUser();
  // Role flags live on permissions, not the hook's top level; canEditSite
  // (pliny.change_site) mirrors the backend gate on the bulk site actions.
  const { isODLEngineering, isAtLeastSiteGroupAdmin, canEditSite } = permissions;

  // Customer-type relay-load term ("Panels"/"Zones"/"Faces") for the faces column
  // title; falls back to the i18n default ("Faces") outside a term'd customer.
  // The customer prop (not scope) feeds resolution, so flat-route mounts (the
  // services tier) get the CustomerType override too.
  const relayLoadTerm = useRelayLoadTerm(undefined, customer);
  // Site term ("Site"/"Structure"), resolved CustomerType override → tenant term
  // → i18n default.
  const siteTerm = useSiteTerm(customer);

  // Reference catalogs (session-cached, loaded once at boot) for the
  // dimension filters and alarm labels.
  const alarmCategories = useSelector(selectAlarmCategories);
  const alarmDefinitions = useSelector(selectAlarmDefinitions);

  const customerId = customer.id;
  const siteGroupId = selectedPlant.id;
  const isBillboard = customer.is_billboard ?? true;
  const isVirtual = selectedPlant.is_virtual ?? false;
  const canCreateSites = userPermCheck(user, 'pliny.add_site');

  // Every row affordance — name cell, mobile card, trailing select action —
  // deep-links to the same destination. The customer tier goes to the site
  // dashboard; a tier that mounts this grid under its own route supplies its
  // own target.
  const resolveSitePath = useCallback(
    row =>
      sitePath ? sitePath(row) : `/c/${customerId}/plant/${siteGroupId ?? row.site_group?.id}/structure/${row.id}`,
    [sitePath, customerId, siteGroupId],
  );

  // Alarm-namespace translator scoped to THIS grid's customer (the scope
  // prop, not the legacy selected-customer store) so commercial tenants get
  // their `_commercial` locale variants.
  const tAlarm = useCallback((key, options) => translateAlarm(t, key, isBillboard, options), [t, isBillboard]);

  // id → translated label lookups for the alarm badges. Backend rows carry
  // ids + fallback display strings; the reference catalogs supply the
  // locale_key that i18n (with the commercial context) resolves.
  const alarmCategoryLabelById = useMemo(() => {
    const labels = new Map();
    alarmCategories.forEach(cat => {
      labels.set(String(cat.id), tAlarm(cat.locale_key, { defaultValue: cat.display_name }));
    });
    return labels;
  }, [alarmCategories, tAlarm]);

  const alarmLabelById = useMemo(() => {
    const labels = new Map();
    alarmDefinitions.forEach(alarm => {
      labels.set(String(alarm.id), tAlarm(alarm.locale_key, { defaultValue: alarm.display_name }));
    });
    return labels;
  }, [alarmDefinitions, tAlarm]);

  // CreateSiteDialog still reads the legacy Immutable site-group shape (a
  // toJS()-based check for the require_sites_bill_alternate tenant option) —
  // shim the plain scope value back to Immutable at this boundary so that
  // contract keeps working; CreateSiteDialog owns it, not this grid.
  const legacySiteGroupForCreateDialog = useMemo(() => fromJS(selectedPlant), [selectedPlant]);

  // Stable ref for the grid's refresh fn — captured from onGridReady
  const { onGridReady: captureGridReady, refresh: refreshGrid } = useGridRefreshRef();

  // odl-app Site write mutations backing the bulk selection-bar actions.
  const [deactivateSites] = useDeactivateSitesMutation();
  const [transferSites] = useTransferSitesMutation();
  const [reactivateSites] = useReactivateSitesMutation();

  // --- Filter state ---
  const [statusFilter, setStatusFilter] = usePersistedState(useGridSessionKey(persistScope, 'status'), defaultStatus);

  // Sync defaultStatus → persisted filter when parent changes it (e.g. deactivated view)
  useEffect(() => {
    if (defaultStatus !== statusFilter) setStatusFilter(defaultStatus);
  }, [defaultStatus]);

  // --- Column visibility (controlled; initializes from descriptor defaults) ---
  const columnVisibility = useColumnVisibilityModel(persistScope);
  const statCardVisibility = useStatCardVisibilityModel(persistScope);
  const statCardOrder = useStatCardOrderModel(persistScope);
  const tenantTerms = useTenantTerms();
  // The tenant's vocabulary, plus the i18next context its copy varies by: a
  // commercial tenant reads `_commercial` overrides where the catalogue has
  // them. Every descriptor label resolves through this — an alarm category in
  // a filter option now reads exactly as it reads on the alarm chip beside it.
  const descriptorTerms = useMemo(
    () => ({ ...tenantTerms, context: isBillboard ? undefined : 'commercial' }),
    [tenantTerms, isBillboard],
  );

  // Every dimension filter lives in one model: the params the stat cards bind
  // and the ones the descriptor's controls write are the same params, so a card
  // and the control over it can never disagree about what is applied. It
  // persists under one key, which is also what a select-all bulk action sends.
  // Filters are scoped to the customer being looked at; a column layout or a
  // card order is a preference and is not. What a filter offers is per customer
  // type — one customer's load types are not the next one's — so carrying a
  // selection across a customer switch would filter by something that customer
  // does not have, and coming back restores what was left applied here.
  const cardFilters = useGridCardFilters(customerId ? `${persistScope}:c${customerId}` : persistScope);

  // --- Dialog / bulk-action UI state ---
  const [reactivateRows, setReactivateRows] = useState([]);
  const [deactivateSiteRows, setDeactivateSiteRows] = useState([]);
  const [deactivateSelectAll, setDeactivateSelectAll] = useState(false);
  const [deactivateDialogCount, setDeactivateDialogCount] = useState(0);
  const [transferSiteIds, setTransferSiteIds] = useState([]);
  const [transferSelectAll, setTransferSelectAll] = useState(false);
  const [transferDialogCount, setTransferDialogCount] = useState(0);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);

  // --- Stats state ---

  // --- Param assembly ---
  // Scope = customer/plant + active/deactivated status. Drives the stats call
  // (which must NOT see the dimension filters, or the alarm/carrier option
  // counts would collapse to the current selection).
  const scopeParams = useMemo(() => {
    const params = { site_group_id: siteGroupId, customer_id: customerId };
    if (statusFilter === 'deactivated') params.deactivated = true;
    else if (statusFilter !== 'all') params.deactivated = false;
    return params;
  }, [siteGroupId, customerId, statusFilter]);

  // Dimension filters as raw field keys; the config serializer wraps them as
  // `filter[<key>]`. Shared by the grid extraParams and the bulk select-all
  // payload so a select-all action targets exactly the filtered set.
  const dimensionFilters = cardFilters.params;

  // --- extraParams for EntityGrid (scope + active dimension filters) ---
  // Tier extension params (e.g. the services job-status filter) ride at the
  // dimension level: they narrow the list and the select-all bulk filters,
  // never the scope-level stats call.
  const extraParams = useMemo(
    () => ({ ...scopeParams, ...dimensionFilters, ...(extension?.params ?? {}) }),
    [scopeParams, dimensionFilters, extension?.params],
  );

  // Report the composed list params upward so a tier extension can aim its
  // own sibling fetches (e.g. the services stat cards) at exactly the set
  // the grid is listing.
  useEffect(() => {
    extension?.onParamsChange?.(extraParams);
  }, [extraParams, extension?.onParamsChange]);

  // Filter dict for a select-all bulk action: the exact JSON:API params
  // (filter[<field>] keys) the grid sends to odl-app/sites/, so the action
  // targets precisely the currently-filtered set.
  const bulkFilters = useMemo(() => toJsonApiParams(extraParams), [extraParams]);

  // Select-all clears whenever the scope or any filter changes.
  const {
    selectAll: selectAllOption,
    active: selectAllActive,
    onClear: clearSelectAll,
  } = useGridSelectAll(extraParams);

  // --- Stats fetch (odl-app endpoint, scoped to customer/plant + status) ---
  const statsParams = useMemo(() => toJsonApiParams(scopeParams), [scopeParams]);
  const { stats: siteStats, isLoading: statsLoading } = useGridStats(fetchSitesStats, statsParams, {
    enabled: Boolean(siteGroupId),
  });

  // --- Grid lifecycle ---
  const handleGridReady = useCallback(
    api => {
      captureGridReady(api);
      extension?.onGridReady?.(api);
    },
    [captureGridReady, extension?.onGridReady],
  );

  // --- Reactivation ---
  const closeReactivateDialog = useCallback(() => setReactivateRows([]), []);

  const confirmReactivation = useCallback(async () => {
    const result = await reactivateSites({ siteIds: reactivateRows.map(r => r.id) });
    if (result.error) {
      dispatch(
        addToast({
          title: t('general.error', 'Error'),
          message: t('sites.reactivateFailed', 'Failed to reactivate'),
          level: 'error',
          dismissible: true,
        }),
      );
      return;
    }
    dispatch(
      addToast({
        title: t('general.success', 'Success'),
        message: t('sites.reactivated', 'Reactivated'),
        level: 'success',
        dismissible: true,
        autoDismiss: 5,
      }),
    );
    closeReactivateDialog();
    refreshGrid();
  }, [reactivateSites, reactivateRows, closeReactivateDialog, dispatch, t, refreshGrid]);

  // --- Deactivation ---
  const closeDeactivateDialog = useCallback(() => {
    setDeactivateSiteRows([]);
    setDeactivateSelectAll(false);
  }, []);

  const confirmDeactivation = useCallback(
    async ({ radio_action }) => {
      const result = await deactivateSites({
        siteIds: deactivateSiteRows.map(r => r.id),
        selectAll: deactivateSelectAll,
        filters: bulkFilters,
        radioAction: radio_action,
      });
      if (result.error) return;
      closeDeactivateDialog();
      clearSelectAll();
      refreshGrid();
    },
    [
      deactivateSiteRows,
      deactivateSites,
      deactivateSelectAll,
      bulkFilters,
      closeDeactivateDialog,
      clearSelectAll,
      refreshGrid,
    ],
  );

  // --- Transfer ---
  const confirmTransfer = useCallback(
    async targetSiteGroupId => {
      const result = await transferSites({
        siteIds: transferSiteIds,
        selectAll: selectAllActive,
        filters: bulkFilters,
        targetSiteGroupId,
      });
      if (result.error) return;
      setTransferSiteIds([]);
      setTransferSelectAll(false);
      clearSelectAll();
      refreshGrid();
    },
    [transferSiteIds, transferSites, selectAllActive, bulkFilters, clearSelectAll, refreshGrid],
  );

  // --- fieldOverrides (render-only; sizing/order/headers come from descriptor) ---
  const fieldOverrides = useMemo(
    () => ({
      name: {
        cellClassName: 'cell-pinned',
        renderCell: ({ row, value }) => (
          <GridNameCell
            to={resolveSitePath(row)}
            primary={value}
            secondary={row.description}
            spacing={1}
            linkSx={{ py: 0.5, minWidth: 0, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}
            leading={<UnitStatusDot status={row.status} />}
            trailing={renderAddonIcons(row, t)}
          />
        ),
      },
      alarms: {
        renderCell: ({ row }) => (
          <AlarmChipCell alarms={resolveAlarmBadges(row, alarmCategoryLabelById, alarmLabelById)} />
        ),
      },
      last_alarm: {
        valueGetter: (_value, row) => getLastAlarm(row),
        renderCell: ({ value }) =>
          value ? (
            <Typography variant="body2" fontVariantNumeric="tabular-nums" noWrap>
              {formatDateTime(value, user)}
            </Typography>
          ) : (
            <Typography variant="caption" color="text.disabled">
              {'—'}
            </Typography>
          ),
      },
      device_label: {
        renderCell: ({ row, value }) => (
          <Box sx={{ py: 0.5 }}>
            <Typography variant="body2" noWrap>
              {value}
            </Typography>
            {row.iccid && (
              <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
                {row.iccid}
              </Typography>
            )}
          </Box>
        ),
      },
      // Tier extension render overrides (e.g. the services job-status chip)
      // layer over — and may replace — the base overrides above.
      ...(extension?.fieldOverrides ?? {}),
    }),
    [resolveSitePath, t, user, alarmCategoryLabelById, alarmLabelById, extension?.fieldOverrides],
  );

  // Export overrides: `alarms` and `last_alarm` are virtual columns the
  // cells derive from the alarm_states payload, so the file restates the
  // same derivations. description and iccid need nothing here — the
  // descriptor marks them export:"always" beside name / device_label.
  const exportOptions = useMemo(
    () => ({
      fileName: 'sites.csv',
      columns: {
        alarms: {
          valueGetter: row =>
            resolveAlarmBadges(row, alarmCategoryLabelById, alarmLabelById)
              .map(badge => badge.label)
              .join(', '),
        },
        last_alarm: {
          valueGetter: row => {
            const timestamp = getLastAlarm(row);
            return timestamp ? formatDateTime(timestamp, user) : '';
          },
        },
      },
    }),
    [user, alarmCategoryLabelById, alarmLabelById],
  );

  // Term resolution for descriptor labels carrying a `label_term`.

  // A tier extension may replace the stat cards (node) or drop them (null);
  // undefined lets EntityGrid build the descriptor's own. The stats fetch stays
  // either way — it also feeds the alarm/carrier filter option counts — so the
  // already-fetched payload is passed through rather than fetched twice.
  const statsHeader = extension?.statsHeader === undefined ? undefined : (extension.statsHeader ?? null);

  // --- Page-owned filters ---
  // Active/deactivated is the one control the descriptor cannot state: it
  // scopes the request rather than narrowing a column, and it is staff-only.
  // Everything else a reader filters by — unit status, alarm category, load
  // type, carrier, model, features — arrives published, already wired to the
  // card-filter model, with its own counts.
  //
  // It scopes the list rather than answering a question about one site, so it
  // reads behind "more filters" with the other administrative controls and
  // leaves the bar to the questions a reader actually asks of a structure.
  const statusFilterDefs = useMemo(
    () =>
      isStaff
        ? [{ ...buildStatusQuickFilter({ t, value: statusFilter, onChange: setStatusFilter }), param: 'deactivated' }]
        : [],
    [t, isStaff, statusFilter, setStatusFilter],
  );

  const quickFilters = NO_PAGE_FILTERS;
  const moreFilters = statusFilterDefs;

  const handleResetAll = useCallback(() => {
    setStatusFilter('active');
    cardFilters.clear();
    extension?.onResetAll?.();
  }, [setStatusFilter, cardFilters, extension?.onResetAll]);

  // A tier extension may demote the base quick chips (site status, unit
  // status, alarms) into the More-filters popover so its own chips lead —
  // the services tier filters by job status first.
  const demoteBaseQuickFilters = Boolean(extension?.demoteBaseQuickFilters);
  const effectiveQuickFilters = useMemo(
    () =>
      demoteBaseQuickFilters ? (extension?.quickFilters ?? []) : [...quickFilters, ...(extension?.quickFilters ?? [])],
    [demoteBaseQuickFilters, quickFilters, extension?.quickFilters],
  );
  const effectiveMoreFilters = useMemo(
    () => (demoteBaseQuickFilters ? [...quickFilters, ...moreFilters] : moreFilters),
    [demoteBaseQuickFilters, quickFilters, moreFilters],
  );

  // Params this page still draws its own control for. The descriptor's list
  // grows as the backend publishes more of it, and a param already drawn here
  // — the unit-status swatches, the counted alarm categories — must not end up
  // with a second control beside it.
  const ownFilterParams = useMemo(
    () => [...effectiveQuickFilters, ...effectiveMoreFilters].map(filter => filter.param).filter(Boolean),
    [effectiveQuickFilters, effectiveMoreFilters],
  );

  // The descriptor's published filters join the bar where the grid asked for
  // them: inline beside the page's own chips, or behind "more filters". The
  // page decides only the order within each group.
  const toolbarContent = useCallback(
    ({ quickFilters: publishedQuick, moreFilters: publishedMore }) => (
      <FilterBar
        quickFilters={[...effectiveQuickFilters, ...omitOwnedFilterControls(publishedQuick, ownFilterParams)]}
        moreFilters={[...effectiveMoreFilters, ...omitOwnedFilterControls(publishedMore, ownFilterParams)]}
        onResetAll={handleResetAll}
      />
    ),
    [effectiveQuickFilters, effectiveMoreFilters, ownFilterParams, handleResetAll],
  );

  // --- Mobile card ---
  // A tier extension contributes its own card sections per row (the services
  // tier's job progress and invoice actions). Its extraColumns are desktop
  // only, so this is the phone's equivalent seam: the tier hands back content,
  // the card owns layout, and this grid never learns what a job is.
  const extensionMobileSections = extension?.mobile?.getSections;
  const renderMobileCard = useCallback(
    row => <SiteListItem row={row} to={resolveSitePath(row)} extraSections={extensionMobileSections?.(row) ?? null} />,
    [resolveSitePath, extensionMobileSections],
  );

  // --- Toolbar actions ---
  // A tier extension may drop the write/clone affordances wholesale — the
  // services tier picks work sites, it doesn't create, clone, or upload them.
  const hideWriteActions = Boolean(extension?.hideWriteActions);
  const hasUpload = !hideWriteActions && isStaff && !Capacitor.isNativePlatform() && customerId && !isMobile;
  const hasClone =
    !hideWriteActions &&
    !Capacitor.isNativePlatform() &&
    (isStaff || isODLEngineering || isAtLeastSiteGroupAdmin) &&
    siteGroupId;

  const toolbarActions =
    hasUpload || hasClone ? (
      <>
        {hasUpload && <UploadGroupDataDialog customerId={customerId} siteGroupId={siteGroupId} />}
        {hasClone && (
          <CloneUnitModal
            triggerItem={
              <ToolbarIconButton tooltip={t('sites.cloneSettings', 'Clone Settings')}>
                <ContentCopyIcon sx={{ fontSize: 18 }} />
              </ToolbarIconButton>
            }
          />
        )}
      </>
    ) : null;

  // --- Create action ---
  const createAction =
    canCreateSites && !isVirtual && !hideWriteActions
      ? {
          label: t('general.create', 'Create') + ' ' + siteTerm.singular,
          onClick: () => setIsCreateDialogOpen(true),
        }
      : undefined;

  // The descriptor's trailing "select" action deep-links to the same
  // destination as the name cell and mobile card. Rendered as a contained
  // button (variant: 'button') rather than the default icon so it reads as
  // the row's primary affordance.
  const actionHandlers = useMemo(
    () => ({
      select: {
        onClick: row => navigate(resolveSitePath(row)),
        variant: 'button',
      },
    }),
    [navigate, resolveSitePath],
  );

  // --- Selection actions ---
  // apiRef (third arg from EntityGrid) resolves positional infinite-scroll IDs
  // to real rows via getRow — grid row keys are positional (_gridIdx), not
  // backend site ids, so direct use of selectedIds as site ids would be wrong.
  const selectionActions = useCallback(
    (selection, _clearSelection, apiRef) => {
      const { selectAll, activeRows, deactivatedRows } = resolveSelectionRows(selection, apiRef);
      const showActiveActions = selectAll || activeRows.length > 0;
      const actionSx = SELECTION_ACTION_SX;

      // New-ticket: staff-only, never on native, only for a single active site
      const canCreateTicket =
        isStaff && !Capacitor.isNativePlatform() && !selectAll && activeRows.length === 1 && siteGroupId;

      // Best-effort total for dialog count on select-all (from MUI X pagination state)
      const getTotal = () => apiRef.current?.state?.pagination?.rowCount ?? 0;

      return (
        <>
          {canCreateTicket && (
            <Tooltip title={t('general.staffOnly', 'Staff Only')}>
              <Button
                size="small"
                startIcon={<ConfirmationNumberOutlinedIcon />}
                endIcon={<LockOutlinedIcon sx={{ fontSize: '0.875rem !important', opacity: 0.6 }} />}
                onClick={() =>
                  navigate(`/c/${customerId}/plant/${siteGroupId}/structure/${activeRows[0].id}/ticket/new`)
                }
                sx={actionSx}
              >
                {t('general.newTicket', 'New Ticket')}
              </Button>
            </Tooltip>
          )}
          {showActiveActions && canEditSite && (
            <>
              <Button
                size="small"
                startIcon={<SwapHorizIcon />}
                onClick={() => {
                  setTransferSelectAll(selectAll);
                  setTransferSiteIds(selectAll ? [] : activeRows.map(r => r.id));
                  setTransferDialogCount(selectAll ? getTotal() : activeRows.length);
                }}
                sx={actionSx}
              >
                {t('actions.transfer', 'Transfer')}
              </Button>
              <Button
                size="small"
                startIcon={<DeleteOutlineIcon />}
                onClick={() => {
                  setDeactivateSelectAll(selectAll);
                  setDeactivateSiteRows(selectAll ? [] : activeRows);
                  setDeactivateDialogCount(selectAll ? getTotal() : activeRows.length);
                }}
                sx={actionSx}
              >
                {t('actions.delete', 'Delete')}
              </Button>
            </>
          )}
          {deactivatedRows.length > 0 && canEditSite && (
            <Button
              size="small"
              startIcon={<RestoreIcon />}
              onClick={() => setReactivateRows(deactivatedRows)}
              sx={actionSx}
            >
              {t('actions.reactivateSite', 'Reactivate {{term}}', { term: siteTerm.singular })}
            </Button>
          )}
        </>
      );
    },
    [isStaff, canEditSite, navigate, customerId, siteGroupId, t, siteTerm],
  );

  return (
    <>
      <EntityGrid
        basePath={basePath}
        fieldOverrides={fieldOverrides}
        actionHandlers={actionHandlers}
        extraParams={extraParams}
        specParams={scopeParams}
        options={{
          searchPlaceholder: t('sites.searchSites', 'Search {{term}}', { term: siteTerm.plural }),
          persistKey: persistScope,
          infiniteScroll: true,
          createAction,
          toolbarContent,
          toolbarActions,
          selection: {
            enabled: true,
            actions: selectionActions,
            selectAll: selectAllOption,
          },
          onGridReady: handleGridReady,
          statsHeader,
          statCards: {
            stats: siteStats,
            isLoading: statsLoading,
            visibility: statCardVisibility,
            order: statCardOrder,
            filters: cardFilters,
          },
          terms: descriptorTerms,
          mobile: { renderCard: renderMobileCard },
          extraColumns: extension?.extraColumns,
          excludeFields: extension?.excludeFields,
          columnVisibility,
          countNoun: siteTerm.plural,
          gridProps: extension?.gridProps,
          export: exportOptions,
        }}
      />

      <WarningDialog
        open={reactivateRows.length > 0}
        title={t('dialogs.areYouSure')}
        body={
          <Box>
            <Typography sx={{ mb: 1.5 }}>
              {t('actions.reactivateSite', 'Reactivate {{term}}', { term: siteTerm.singular })}
            </Typography>
            {reactivateRows.map(row => (
              <Box key={row.id} sx={{ mb: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                  {row.name}
                </Typography>
                {row.description ? (
                  <Typography variant="body2" color="text.secondary">
                    {row.description}
                  </Typography>
                ) : null}
              </Box>
            ))}
          </Box>
        }
        onConfirm={confirmReactivation}
        onCancel={closeReactivateDialog}
      />

      <BulkDeactivateSitesDialog
        open={deactivateSiteRows.length > 0 || deactivateSelectAll}
        onConfirm={confirmDeactivation}
        onClose={closeDeactivateDialog}
        siteRows={deactivateSiteRows}
        selectAll={deactivateSelectAll}
        filters={bulkFilters}
        count={deactivateDialogCount}
        isBillboard={isBillboard}
      />

      <TransferSitesDialog
        open={transferSiteIds.length > 0 || transferSelectAll}
        onClose={() => {
          setTransferSiteIds([]);
          setTransferSelectAll(false);
        }}
        onConfirm={confirmTransfer}
        customerId={customerId}
        currentSiteGroupId={siteGroupId}
        count={transferDialogCount}
      />

      <CreateSiteDialog
        open={isCreateDialogOpen}
        onClose={() => setIsCreateDialogOpen(false)}
        onSuccess={newSite => {
          setIsCreateDialogOpen(false);
          navigate(`/c/${customerId}/plant/${siteGroupId}/structure/${newSite.id}`);
        }}
        siteGroup={legacySiteGroupForCreateDialog}
        customerId={customerId}
        showExtraDetails
      />
    </>
  );
}
